<template>
  <div class="page-section" :class="{ 'tu-scope': getLoggedInUserDashboard === DASHBOARD_TYPES.CARECAMPUS }">
    <div :class="containerClass" class="dash-wrap">
      <h2 class="mb-2">Welcome {{ getLoggedInUser.first_name }}!</h2>
      <p class="mb-4" v-if="getLoggedInUserDashboard === DASHBOARD_TYPES.CARECAMPUS">
        Explore paths, compare programs and take your next step in healthcare.
      </p>
      <p class="mb-4" v-else-if="getLoggedInUserDashboard === DASHBOARD_TYPES.ORGANIZATION">
        Use this Career Center to navigate your healthcare career journey.
      </p>
      <p class="mb-4" v-else>
        Our Student Center is designed to help you be successful. Follow the tasks listed below.
      </p>

      <div
        v-if="getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL"
        class="cards-background-container cards-background-container--solid"
        :style="panelStyle"
      >
        <div class="row mb-0">
          <div class="col-md-4 mb-3" v-for="card in schoolCards" :key="card.title">
            <CategoryCard v-bind="card" @click="card.onClick && card.onClick()" />
          </div>
        </div>
      </div>

      <div v-else-if="getLoggedInUserDashboard === DASHBOARD_TYPES.ORGANIZATION">
        <div class="cards-background-container cards-background-container--solid" :style="panelStyle">
          <div class="row mb-0">
            <div class="col-md-4 mb-3" v-for="card in orgCards" :key="card.title">
              <CategoryCard v-bind="card" />
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="getLoggedInUserDashboard === DASHBOARD_TYPES.CARECAMPUS"
        class="cards-background-container cards-background-container--solid"
        :style="{ '--card-brand': 'var(--tu-blue, #3b86f5)' }"
      >
        <div class="row mb-0">
          <div class="col-md-6 mb-3" v-for="card in carecampusCards" :key="card.title">
            <CategoryCard v-bind="card" @click="card.onClick && card.onClick()" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, isEmpty } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '../../../components/Page.vue';
import CategoryCard from '@/components/CategoryCard.vue';
import { DASHBOARD_TYPES } from '@/common/constants';
import { cardIcon } from '@/common/dashboardCardIcons';
import {
  ACADEMIC_EXCELLENCE_OPTIONS,
  SKILLS_DEVELOPMENT_OPTIONS,
  CAREER_OPPORTUNITIES_OPTIONS,
  CAREER_PREPARATION_OPTIONS,
  getCategoryTaskCount,
  categorizeDashboardItems,
  getCategoryTaskCountFromConfig,
} from '@/common/dashboardCategories';

export default {
  extends: Page,
  name: 'SchoolSetupGuide',
  components: { CategoryCard },
  data() {
    return {
      title: 'Home',

      isLoading: false,
      setupStatuses: {
        uploaded_docs: true,
        question_asked: true,
        resume_build: true,
        payments_paid: true,
        practice_skills: '',
        jobs: '',
        employers: '',
      },
      orgStatuses: {},
      enrolledProgramTitle: '',
      status: null,
      windowWidth: window.innerWidth,
      showPrivacyModal: false,
      DASHBOARD_TYPES,

      // Default cards used when the org has no saved std_dashboard_config yet.
      defaultOrgDashboardConfig: [
        {
          id: 1,
          title: 'Programs',
          description: 'The programs you are applying to and their scorecards.',
          abbr: 'checklist',
          redirectTo: 'student-checklist',
          icon: 'assignment_turned_in',
          color: 'info',
          enable: true,
        },
        {
          id: 2,
          title: 'Community',
          description: 'Connect and grow with others.',
          abbr: 'community',
          redirectTo: 'student-discussions',
          icon: 'people',
          color: 'primary',
          enable: true,
        },
        {
          id: 3,
          title: 'Advisors',
          description: 'Connect with advisors for guidance.',
          abbr: 'advisors',
          redirectTo: 'student-advisors',
          icon: 'supervisor_account',
          color: 'success',
          enable: true,
        },
        {
          id: 4,
          title: 'Explore',
          description: 'Discover resources and more.',
          abbr: 'explore',
          redirectTo: 'student-community',
          icon: 'explore',
          color: 'primary',
          enable: true,
        },
        {
          id: 10,
          title: 'Exam Results',
          description: 'Report your credential exam results.',
          abbr: 'exam_results',
          redirectTo: 'student-exam-results',
          icon: 'score',
          color: 'primary',
          enable: true,
        },
        {
          id: 11,
          title: 'My Licenses',
          description: 'Add your CNA or medication aide license for verification.',
          abbr: 'license_verifications',
          redirectTo: 'student-license-verifications',
          icon: 'card_membership',
          color: 'primary',
          enable: true,
        },
      ],

      organizationDashboard: [
        {
          id: 2,
          title: 'Take a Career Assessment',
          abbr: 'career_assessment',
          btnText: 'Complete',
          description: 'Complete a Basic Assessment to earn 1 point or an Advanced Assessment to earn 5.',
          enable: true,
          icon: 'assessment',
          redirectTo: '',
        },
        {
          title: 'Add Education to Resume',
          abbr: 'education_resume',
          btnText: 'Complete',
          description: 'Earn 1 point for every education entry added to your resume.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Add Work Experience to Resume',
          abbr: 'work_experience_resume',
          btnText: 'Complete',
          description: 'Earn 1 point for each work experience entry you include in your resume.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Add Volunteer Work Experience to Resume',
          abbr: 'volunteer_experience_resume',
          btnText: 'Complete',
          description: 'Earn 1 point for each volunteer work experience entry you include in your resume.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Add Skills to Resume',
          abbr: 'skills_resume',
          btnText: 'Complete',
          description: 'Earn 1 point for each skill entry to your resume.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Add Latest Title and Location to Resume',
          abbr: 'latest_title_location_resume',
          btnText: 'Complete',
          description: 'Earn up to 2 points for adding your current job title and location.',
          enable: true,
          icon: 'description',
          redirectTo: 'std-manage-resume',
        },
        {
          title: 'Practice Interview Simulations',
          abbr: 'practice_skill_simulations',
          btnText: 'Complete',
          description: 'Earn 3 points for completing an interview.',
          enable: true,
          icon: 'mic',
          redirectTo: 'student-simulations-listing',
        },
        {
          title: 'Add Certifications',
          abbr: 'add_certifications',
          btnText: 'Complete',
          description: 'Earn 5 points for each certification you upload to your profile.',
          enable: true,
          icon: 'assignment_turned_in',
          redirectTo: 'add-certificate',
        },
        {
          title: 'Add New Job Offers',
          abbr: 'add_new_offers',
          btnText: 'Complete',
          description: 'Earn 3 points for every offers entry you include to demonstrate career progress.',
          enable: true,
          icon: 'work',
          redirectTo: 'add-offer',
        },
      ],
    };
  },

  computed: {
    ...mapGetters('navbarConfig', ['getLinkedEntity']),
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),
    ...mapGetters('school', ['getPaymentStatus', 'getInAppPaymentStatus']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Setup Guide', active: true },
      ];
    },
    // School/org color for the solid panel behind the cards; also drives the card arrow color.
    panelStyle() {
      const color = get(this.getLoggedInUser, 'linked_entity.primary_color');
      return color ? { '--card-brand': color } : {};
    },
    isMobileScreen() {
      return this.windowWidth <= 600;
    },
    schoolCards() {
      return [
        {
          title: 'Academics',
          description: 'Manage course requirements and track progress.',
          icon: 'school',
          color: 'primary',
          onClick: () => this.navigateToCategory('academic'),
        },
        {
          title: 'Skills',
          description: 'Build, practice, and assess your clinical skills.',
          icon: 'volunteer_activism',
          color: 'warning',
          onClick: () => this.navigateToCategory('skills_development'),
        },
        {
          title: 'Careers',
          description: 'Explore companies, jobs, and sponsorship opportunities.',
          icon: 'work',
          color: 'success',
          onClick: () => this.navigateToCategory('career_opportunities'),
        },
        {
          title: 'Career Prep',
          description: 'Build skills and prepare for career success.',
          icon: 'trending_up',
          color: 'info',
          onClick: () => this.navigateToCategory('career_preparation'),
        },
        {
          title: 'Resources',
          description: 'Explore resources and guides.',
          icon: 'folder_open',
          color: 'primary',
          redirectTo: 'student-community',
        },
      ];
    },
    orgCards() {
      const enableDiscussions = get(this.getLoggedInUser, 'linked_entity.enable_discussions');
      // Fall back to defaults when the org has no saved config yet (e.g. not migrated / stale payload).
      const config = this.dashboardConfig.length ? this.dashboardConfig : this.defaultOrgDashboardConfig;
      return config
        .filter((item) => {
          if (!item.enable) return false;
          // Community card additionally requires discussions to be enabled on the org.
          if (item.abbr === 'community') return !!enableDiscussions;
          return true;
        })
        .map((item) => ({
          title: item.title,
          description: item.description,
          icon: cardIcon(item.abbr, item.icon),
          color: item.color || 'primary',
          redirectTo: item.redirectTo,
        }));
    },
    // Same names and order as the TransitionU top menu.
    carecampusCards() {
      return [
        {
          title: 'Paths',
          description: 'Compare routes from your first credential to your next role.',
          icon: 'trending_up',
          color: 'primary',
          redirectTo: 'student-pathways',
        },
        {
          title: 'Acceptees',
          description: 'Nursing school acceptees and advisors who can help.',
          icon: 'groups',
          color: 'primary',
          redirectTo: 'student-advisors',
        },
        {
          title: 'Education',
          description: 'Compare programs and find schools.',
          icon: 'school',
          color: 'primary',
          onClick: () => this.navigateToCategory('education'),
        },
        {
          title: 'My Info',
          description: 'Build your credential profile.',
          icon: 'info',
          color: 'primary',
          redirectTo: 'student-info',
        },
      ];
    },
    userSettings() {
      return {
        requiresPayment: this.getPaymentStatus,
        requiresInAppPayment: this.getInAppPaymentStatus,
        aiSkillsEnabled: this.getLoggedInUser?.linked_entity?.ai_skills_enabled || false,
      };
    },
    // Get dashboard config from API
    dashboardConfig() {
      return get(this.getLoggedInUser, 'linked_entity.std_dashboard_config.dashboard_config') || [];
    },
    // Categorize dashboard items from API config
    categorizedDashboardItems() {
      return categorizeDashboardItems(this.dashboardConfig);
    },
    academicExcellenceCount() {
      // Use API config for SCHOOL type, otherwise use static config
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL) {
        return getCategoryTaskCountFromConfig(this.categorizedDashboardItems.academic);
      }
      return getCategoryTaskCount(ACADEMIC_EXCELLENCE_OPTIONS, this.getLoggedInUserDashboard, this.userSettings);
    },
    skillsDevelopmentCount() {
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL) {
        return getCategoryTaskCountFromConfig(this.categorizedDashboardItems.skills_development);
      }
      return getCategoryTaskCount(SKILLS_DEVELOPMENT_OPTIONS, this.getLoggedInUserDashboard, this.userSettings);
    },
    careerOpportunitiesCount() {
      // Use API config for SCHOOL type, otherwise use static config
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL) {
        return getCategoryTaskCountFromConfig(this.categorizedDashboardItems.career_opportunities);
      }
      return getCategoryTaskCount(CAREER_OPPORTUNITIES_OPTIONS, this.getLoggedInUserDashboard, this.userSettings);
    },
    careerPreparationCount() {
      // Use API config for SCHOOL type, otherwise use static config
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL) {
        return getCategoryTaskCountFromConfig(this.categorizedDashboardItems.career_preparation);
      }
      return getCategoryTaskCount(CAREER_PREPARATION_OPTIONS, this.getLoggedInUserDashboard, this.userSettings);
    },
  },

  methods: {
    ...mapActions('student', ['getStudentDashboardStatus', 'getDashboardScore', 'getMyStudentProfile']),
    get,
    isEmpty,

    navigateToCategory(categoryType) {
      this.$router.push({ name: 'category-detail', params: { categoryType } });
    },

    loadVoiceflow() {
      var stdBotApiKey = this.getLoggedInUser.linked_entity.std_bot_api_key;
      var id = this.getLoggedInUser.id.toString();
      var v = document.createElement('script'),
        s = document.getElementsByTagName('script')[0];

      v.onload = function() {
        window.voiceflow.chat.load({
          verify: { projectID: stdBotApiKey },
          url: 'https://general-runtime.voiceflow.com',
          versionID: 'production',
          userID: id,
        });
      };

      v.src = 'https://cdn.voiceflow.com/widget/bundle.mjs';
      v.type = 'text/javascript';
      s.parentNode.insertBefore(v, s);
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    async fetchDashboardStatus() {
      this.isLoading = true;
      try {
        this.setupStatuses = (await this.getStudentDashboardStatus()).data;
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    // Program title picks the Skills Development photo (CNA, MA, phlebotomy, EKG, doula).
    async fetchEnrolledProgram() {
      try {
        // Fetch fresh: a cached profile can hold enrolled_program as a bare id without its title.
        const profile = await this.getMyStudentProfile(true);
        this.enrolledProgramTitle = get(profile, 'enrolled_program.title', '');
      } catch (_err) {
        // The generic Skills photo is shown when the program can't be loaded.
      }
    },

    async fetchDashboardScore() {
      this.isLoading = true;
      try {
        this.orgStatuses = (
          await this.getDashboardScore({
            ...(this.getLoggedInUserDashboard !== DASHBOARD_TYPES.CARECAMPUS && {
              id: this.getLoggedInUser.linked_entity.id,
              entity_type: 'organization',
            }),
          })
        ).data;
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },
  },

  watch: {
    getPaymentStatus: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.status = value;
        }
      },
    },
  },

  async mounted() {
    if (this.getLoggedInUser && this.getLoggedInUser.linked_entity) {
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.SCHOOL) {
        this.loadVoiceflow();
        this.fetchDashboardStatus();
        this.fetchEnrolledProgram();
      }
      if (this.getLoggedInUserDashboard === DASHBOARD_TYPES.ORGANIZATION) {
        this.fetchDashboardScore();
      }
    }
    window.addEventListener('resize', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
// One width for every school, org and TransitionU dashboard, whatever page layout the portal uses.
.dash-wrap {
  max-width: 1240px !important;
}

.cards-background-container {
  border-radius: 20px;
  padding: 1.75rem;
  margin-bottom: 2rem;
  background-color: #efefef;
  background-color: color-mix(in srgb, var(--card-brand, #d9d9d9) 14%, #ffffff);
}

// Solid brand color behind the white cards: the school/org color, or TransitionU blue.
.cards-background-container--solid {
  background-color: var(--card-brand, var(--primary-color, #3b86f5));
}

@media (max-width: 768px) {
  .cards-background-container {
    padding: 1rem;
    border-radius: 16px;
  }
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}
</style>
